import { create } from "zustand";
import { persist } from "zustand/middleware";

interface ProjectContextState {
  /** Active wrapper Project UUID. Null when outside any Project. */
  activeProject: string | null;
  /**
   * The schematic version currently pinned for chat / queries. Null = read
   * from current. Set explicitly when a user opens a historical version or
   * clicks "update to latest" on a stale session.
   */
  activeVersionId: string | null;
  activeVersionNumber: number | null;
  /**
   * Active :Circuit within the current project. Multi-Circuit projects
   * (Phase 14) carry several circuits; this is which one the user is
   * currently focused on. Null falls back to the project's default
   * (typically the `main` circuit). Cleared when the project changes
   * — new project, new circuit set.
   */
  activeCircuitId: string | null;
  /**
   * Active Board within the current Project (project/board model —
   * `docs/project-board-model.md`). For single-board Projects this
   * stays null — surfaces fall back to the only Board. For multi-board
   * Projects the picker writes this; per-board surfaces (BOM, PCB,
   * Versions, Circuits) read it.
   *
   * Cleared when the active Project changes.
   */
  activeBoardId: string | null;
}

interface ProjectContextActions {
  setActiveProject: (project: string | null) => void;
  setActiveVersion: (
    versionId: string | null,
    versionNumber: number | null,
  ) => void;
  setActiveCircuit: (circuitId: string | null) => void;
  setActiveBoard: (boardId: string | null) => void;
  clearContext: () => void;
}

export const useProjectStore = create<
  ProjectContextState & ProjectContextActions
>()(
  persist(
    (set, get) => ({
      activeProject: null,
      activeVersionId: null,
      activeVersionNumber: null,
      activeCircuitId: null,
      activeBoardId: null,
      setActiveProject: (project) => {
        const prev = get().activeProject;
        if (prev !== project) {
          // Switching project discards any version + circuit + board
          // pin — the new project's boards/circuits are a fresh set,
          // and its current version is resolved server-side at session
          // creation.
          set({
            activeProject: project,
            activeVersionId: null,
            activeVersionNumber: null,
            activeCircuitId: null,
            activeBoardId: null,
          });
        }
      },
      setActiveVersion: (versionId, versionNumber) =>
        set({ activeVersionId: versionId, activeVersionNumber: versionNumber }),
      setActiveCircuit: (circuitId) => set({ activeCircuitId: circuitId }),
      setActiveBoard: (boardId) => set({ activeBoardId: boardId }),
      clearContext: () =>
        set({
          activeProject: null,
          activeVersionId: null,
          activeVersionNumber: null,
          activeCircuitId: null,
          activeBoardId: null,
        }),
    }),
    { name: "circuit-forge-project-context" },
  ),
);
